import { NOTICE_COMMENT_CHANGE } from 'Modules/Communication/Notices/shared/store/notice/actionTypes'
import { EMAILS_FETCH_NEW_SILENTLY_REQUESTED } from 'shared/store/communication/emails/actionTypes'

import { MeetingNoticeInviteReply } from 'Modules/Boardwork/Meetings/shared/models/MeetingNoticeInviteReply'
import { fetchCaseSilent } from 'Modules/Boardwork/Meetings/shared/store/case'
import { checkCaseAndUpdateIfInAgenda } from 'Modules/Boardwork/Meetings/shared/store/manage/agenda'
import {
  fetchMeetingSilentRequest,
  lastPersonSigned,
  meetingNoticeEmailSent,
  signingCompleted,
} from 'Modules/Boardwork/Meetings/shared/store/meeting'
import { handleInsuranceClaimTransferStatusMessageThunk } from 'Modules/HousingAssociation/Insurance/shared/store/insuranceClaim/reducer'
import { useRef } from 'react'
import { portalBellNotifications } from 'shared/constants/notifications'
import {
  ISocketMessage,
  BoardMeetingNotificationTypeEnum as NotificationTypeEnum,
  SocketMessageTypeEnum,
} from 'shared/models/Socket'
import { addNotification } from 'shared/store/notifications'
import { SEND_EMAIL_UPDATE_SENDING_STATUS_REQUESTED } from 'shared/store/sendMessage/email/actionTypes'
import { SEND_SMS_UPDATE_SENDING_STATUS_REQUESTED } from 'shared/store/sendMessage/sms/actionTypes'

const useSignalRMessage = () => {
  const locationRef = useRef<string>('/')

  // window.location doesnt re-render the functions.
  // So we need to use a referance that updates instead.
  locationRef.current = window.location.href

  const signalRMessageChecker = (message: ISocketMessage) => {
    // Just check message connects to the meeting types
    const isPayloadType = (type: NotificationTypeEnum) =>
      type === message?.Notification?.Payload?.Type

    if (portalBellNotifications.includes(message.NotificationType))
      return addNotification

    switch (message.NotificationType) {
      // Unloc
      // case SocketMessageTypeEnum.UnlocLockAccessCreated:
      //   return DIGITAL_KEYS_DOORS_ADDED
      // case SocketMessageTypeEnum.UnlocOwnerAccessCreated:
      //   return DIGITAL_KEYS_OWNER_ADDED
      // case SocketMessageTypeEnum.UnlocCreateAccessFailed:
      //   return DIGITAL_KEYS_ACCESS_LIST_QUEUE_ERROR

      // Inbox
      case SocketMessageTypeEnum.InboxNewEmail:
        return EMAILS_FETCH_NEW_SILENTLY_REQUESTED
      case SocketMessageTypeEnum.ReceiptSendingStatus:
        return message.Notification.Payload?.NotificationMessage?.toLowerCase()?.includes(
          'sms'
        )
          ? SEND_SMS_UPDATE_SENDING_STATUS_REQUESTED
          : SEND_EMAIL_UPDATE_SENDING_STATUS_REQUESTED

      // Notice
      case SocketMessageTypeEnum.NoticeCommentChange:
        return NOTICE_COMMENT_CHANGE

      // Insurance claims
      case SocketMessageTypeEnum.InsuranceCaseDraftTransferDone:
      case SocketMessageTypeEnum.InsuranceCaseDraftTransferFailed:
        return handleInsuranceClaimTransferStatusMessageThunk

      // Meetings
      case SocketMessageTypeEnum.BoardMeetingNoticeEmailSent:
        return meetingNoticeEmailSent.type

      case SocketMessageTypeEnum.BoardMeetingNotification: {
        // Is caseId if type = MeetingCaseUpdated
        // Is meetingId if type = MeetingUpdated
        const messageId = message?.Notification?.Payload?.Id

        const isOnPage = locationRef.current.includes(messageId)
        const isCase = locationRef.current.includes(`styrearbeid/moter/saker/`)
        const isMeeting = locationRef.current.includes(
          `/styrearbeid/moter/mote/`
        )

        if (
          isPayloadType(NotificationTypeEnum.MeetingUpdated) &&
          isMeeting &&
          isOnPage
        ) {
          return fetchMeetingSilentRequest.type
        }

        if (isPayloadType(NotificationTypeEnum.MeetingCaseUpdated)) {
          // Per now, we avoid updating in endre, we got the 409 conflict in place anyway.
          if (isOnPage && isCase) {
            // Fetch case contains logic to check if the lastUpdated matches
            // Otherwise it will give a conflict error.
            return fetchCaseSilent.type
          }

          if (isMeeting) return checkCaseAndUpdateIfInAgenda.type
        }

        if (isPayloadType(NotificationTypeEnum.MeetingLastPersonSigned)) {
          return lastPersonSigned.type
        }

        if (isPayloadType(NotificationTypeEnum.MeetingSigningCompleted)) {
          return signingCompleted.type
        }

        return undefined
      }
      default:
        return undefined
    }
  }

  const customPayloadChecker = (
    messageType: string,
    message: ISocketMessage
  ) => {
    switch (messageType) {
      case EMAILS_FETCH_NEW_SILENTLY_REQUESTED:
        return {
          clientId: message?.Notification?.Payload?.ClientId,
          isOnEmailPage:
            locationRef.current.includes('/kommunikasjon/epost') ||
            locationRef.current.includes('/kommunikasjon/innstillinger') ||
            locationRef.current === '/',
        }
      case SEND_EMAIL_UPDATE_SENDING_STATUS_REQUESTED:
        return {
          isOnEmailPage: locationRef.current.includes('/kommunikasjon/epost'),
          ...message,
        }

      case meetingNoticeEmailSent.type: {
        const meetingId = message?.Notification?.Payload?.MeetingId
        const meetingResponse: MeetingNoticeInviteReply = {
          meetingId,
          isSuccess: message?.Notification?.Payload?.Success,
        }
        // Check if meetingId is string
        const isMeetingIdString = typeof meetingId === 'string'

        if (!isMeetingIdString) return null
        return meetingResponse
      }

      case fetchCaseSilent.type:
      case checkCaseAndUpdateIfInAgenda.type:
      case fetchMeetingSilentRequest.type:
      case lastPersonSigned.type:
      case signingCompleted.type:
        return message?.Notification?.Payload?.Id

      default:
        return false
    }
  }

  const isBellNotification = (messageType: SocketMessageTypeEnum) => {
    return portalBellNotifications.includes(messageType)
  }

  return {
    signalRMessageChecker,
    customPayloadChecker,
    isBellNotification,
  }
}

export default useSignalRMessage
